import { BaseCard, CardSection, ErrorCard } from "@fero/components";
import {
  usePurchaserSavedDeliveryInfoListQuery,
  useSaveNominatedPurchaserDeliveryAddressQuery,
} from "@fero/hooks";
import type { UpdateSavedDeliveryInfo } from "@fero-shared/api-contracts";
import EditIcon from "@mui/icons-material/EditOutlined";
import { IconButton, Skeleton, Stack, Typography } from "@mui/material";
import type React from "react";
import { useState } from "react";
import { SavedDeliveryAddressModal } from "./saved-delivery-address-modal";

interface SavedDeliveryAddressCardProps {
  nominatedPurchaserId: string;
}

export const SavedDeliveryAddressCard: React.FC<
  SavedDeliveryAddressCardProps
> = (props) => {
  const { nominatedPurchaserId } = props;
  const {
    data: deliveryInfoList = [],
    isLoading,
    isError,
  } = usePurchaserSavedDeliveryInfoListQuery({ nominatedPurchaserId });
  const [editDeliveryDetailsOpen, setEditDeliveryDetailsOpen] = useState(false);
  const saveDeliveryInfoMutation =
    useSaveNominatedPurchaserDeliveryAddressQuery();

  const handleSubmit = async (savedDeliveryInfo: UpdateSavedDeliveryInfo) => {
    await saveDeliveryInfoMutation.mutateAsync({
      params: { nominatedPurchaserId },
      body: savedDeliveryInfo,
    });
    setEditDeliveryDetailsOpen(false);
  };

  if (isLoading) {
    return <Skeleton variant="rounded" height={250} animation="wave" />;
  }

  if (isError) {
    return <ErrorCard message="Error loading purchaser delivery info" />;
  }

  // Assume one address for now, we can setup viewing/editing multiple later
  const [deliveryInfo] = deliveryInfoList;

  if (!deliveryInfo) {
    return (
      <BaseCard spacing={3}>
        <Typography variant="h6" fontWeight={500} fontSize={20}>
          Delivery Details
        </Typography>
        <Typography>No saved delivery details</Typography>
      </BaseCard>
    );
  }
  return (
    <BaseCard spacing={3}>
      <Stack
        direction="row"
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Typography variant="h6" fontWeight={500} fontSize={20}>
          Delivery Details
        </Typography>
        <IconButton onClick={() => setEditDeliveryDetailsOpen(true)}>
          <EditIcon />
        </IconButton>
      </Stack>
      <SavedDeliveryAddressModal
        isOpen={editDeliveryDetailsOpen}
        onClose={() => setEditDeliveryDetailsOpen(false)}
        onSubmit={(savedDeliveryInfo: UpdateSavedDeliveryInfo) =>
          handleSubmit(savedDeliveryInfo)
        }
        initialData={deliveryInfo}
      />
      {deliveryInfo.contact && (
        <CardSection sectionTitle="Contact">
          <Typography>{deliveryInfo.contact.name}</Typography>
          <Typography>{deliveryInfo.contact.email}</Typography>
          <Typography>{deliveryInfo.contact.phoneNumber}</Typography>
        </CardSection>
      )}

      {deliveryInfo.address && (
        <CardSection sectionTitle="Default Address">
          <Typography>{deliveryInfo.deliveryPointName}</Typography>
          <Typography>{deliveryInfo.address.streetName}</Typography>
          {deliveryInfo.address.line1 && (
            <Typography>{deliveryInfo.address.line1}</Typography>
          )}
          {deliveryInfo.address.line2 && (
            <Typography>{deliveryInfo.address.line2}</Typography>
          )}
          <Typography>
            {deliveryInfo.address.town}, {deliveryInfo.address.country}
          </Typography>
          <Typography>{deliveryInfo.address.postCode}</Typography>
        </CardSection>
      )}

      {deliveryInfo.instruction && (
        <CardSection sectionTitle="Delivery Instructions">
          <Typography>{deliveryInfo.instruction}</Typography>
        </CardSection>
      )}
    </BaseCard>
  );
};
